import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearFileSession } from '@/lib/fileSession';
import { User, Search, Menu, X, ChevronDown, ChevronRight, UserPlus, LogOut, LayoutDashboard, Settings, UserRound, FileText, BookOpen, GraduationCap, Play, Award, BookMarked, ArrowRight, Landmark, MapPin, Newspaper, CalendarDays, MessageSquare, Download, Bell, Headphones, Stethoscope } from 'lucide-react';
import { isInstallAvailable, triggerInstall, onInstallAvailable } from './PWAInstallPrompt';
import { useSiteVisibility, useMenuRegions } from '@/lib/siteVisibility';

const DEFAULT_REGIONS = [
  { label: 'ALSACE', slug: 'alsace' },
  { label: 'ATLANTIQUE', slug: 'atlantique' },
  { label: 'AUVERGNE', slug: 'auvergne' },
  { label: 'CONTACTO ILE DE France', slug: 'contacto-idf' },
  { label: 'COTE D\'AZUR', slug: 'cote-dazur' },
  { label: 'LANGUEDOC-ROUSSILLON', slug: 'languedoc-roussillon' },
  { label: 'LORRAINE', slug: 'lorraine' },
  { label: 'OCCITANIE', slug: 'occitanie' },
  { label: 'PROVENCE', slug: 'provence' },
  { label: 'SCALP', slug: 'scalp' },
  { label: 'SEPTENTRION', slug: 'septentrion' },
  { label: 'VIKING', slug: 'viking' },
];

const SiteHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTransparent, setIsTransparent] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileRegionsOpen, setMobileRegionsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showRégions, setShowRégions] = useState(false);
  const dropdownTimeout = useRef(null);
  const regionsTimeout = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isOphtalmo = user?.role === 'ophthalmologist';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'regional';
  const [canInstall, setCanInstall] = useState(isInstallAvailable());
  const REGIONS = useMenuRegions(DEFAULT_REGIONS);
  const { isHidden } = useSiteVisibility();

  useEffect(() => {
    return onInstallAvailable((available) => setCanInstall(available));
  }, []);

  // Always white header
  useEffect(() => {
    setIsTransparent(false);
  }, [location.pathname]);

  const allMenuItems = [
    { label: "L'ASSOCIATION", children: [
      { label: 'Le bureau', to: '/bureau', icon: Landmark },
      { label: 'Les regions', to: null, hasSubmenu: true, icon: MapPin },
    ]},
    { label: 'NOTRE ACTU', children: [
      { label: 'Blog', to: '/blog', icon: Newspaper },
      { label: 'Agenda / Congrès', to: '/events', icon: CalendarDays },
      { label: "L'actu LinkedIn", to: '/actu', icon: ArrowRight, isCta: true },
    ]},
    { label: 'FORMATION', to: '/nos-formations', children: [
      { label: 'Les rapports', to: '/nos-formations?cat=Les Rapports', icon: FileText },
      { label: 'Les publications scientifiques', to: '/nos-formations?cat=Les Publications', icon: BookOpen },
      { label: 'Les cours', to: '/nos-formations?cat=Les Cours', icon: GraduationCap },
      { label: 'Les replays', to: '/nos-formations?cat=Les Replays', icon: Play },
      { label: 'Le grand prix de contactologie', to: '/nos-formations?cat=Grand Prix', icon: Award },
      { label: 'Tous les articles', to: '/nos-formations', icon: ArrowRight, isCta: true },
    ]},
  ];
  // Pages unticked in Admin → Toutes les pages du site disappear from the menus
  const menuItems = allMenuItems
    .filter(item => !(item.to && isHidden(item.to)))
    .map(item => ({ ...item, children: item.children.filter(c => (c.hasSubmenu ? REGIONS.length > 0 : !isHidden(c.to))) }))
    .filter(item => item.children.length > 0);

  const handleDropdownEnter = (label) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };
  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => { setOpenDropdown(null); setShowRégions(false); }, 200);
  };
  const handleRégionsEnter = () => {
    clearTimeout(regionsTimeout.current);
    clearTimeout(dropdownTimeout.current);
    setShowRégions(true);
  };
  const handleRégionsLeave = () => {
    regionsTimeout.current = setTimeout(() => setShowRégions(false), 200);
  };

  const handleLogout = () => {
    clearFileSession();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/auth');
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
      }`} style={{ fontFamily: "'Outfit', 'Roboto', sans-serif" }} data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">

            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex items-center gap-1 flex-shrink-0" data-testid="header-logo">
              <img
                src="/SFOALC-logo.png"
                alt="SFOALC"
                className="h-8 lg:h-9 w-auto transition-all"
                style={isTransparent ? { filter: 'brightness(0) invert(1)' } : {}}
              />
            </button>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {menuItems.map((item) => (
                <div key={item.label} className="relative"
                  onMouseEnter={() => item.children && handleDropdownEnter(item.label)}
                  onMouseLeave={() => item.children && handleDropdownLeave()}
                >
                  <button
                    onClick={() => item.to ? navigate(item.to) : undefined}
                    className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold tracking-[0.12em] uppercase transition-colors ${
                      isTransparent ? 'text-white/90 hover:text-white' : 'text-[#35AFE4] hover:text-[#045474]'
                    }`} data-testid={`menu-${item.label.replace(/\s+/g, '-').toLowerCase()}`}>
                    {item.label}
                    {item.children && <ChevronDown size={14} className={`ml-0.5 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} strokeWidth={2.5} />}
                  </button>

                  {item.children && openDropdown === item.label && (
                    <div className="absolute top-full left-0 pt-2 min-w-[260px] z-50"
                      onMouseEnter={() => handleDropdownEnter(item.label)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <div className="bg-white rounded-xl shadow-2xl border border-gray-100/80 py-2 backdrop-blur-sm">
                        {item.children.map((child, ci) => (
                          child.hasSubmenu ? (
                            <div key="regions" className="relative"
                              onMouseEnter={handleRégionsEnter}
                              onMouseLeave={handleRégionsLeave}
                            >
                              <button className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:text-[#1e3a5f] hover:bg-[#eaf6fc]/60 transition-colors flex items-center gap-3"
                                data-testid="submenu-regions">
                                {child.icon && <child.icon size={15} className="text-[#35AFE4] flex-shrink-0" strokeWidth={2} />}
                                <span className="flex-1">{child.label}</span>
                                <ChevronRight size={14} className="text-gray-300" />
                              </button>
                              {showRégions && (
                                <div className="absolute left-full top-0 ml-1 min-w-[220px] z-[60]"
                                  onMouseEnter={handleRégionsEnter}
                                  onMouseLeave={handleRégionsLeave}
                                >
                                  <div className="bg-white rounded-xl shadow-2xl border border-gray-100/80 py-2 max-h-[70vh] overflow-y-auto backdrop-blur-sm">
                                    {REGIONS.map(r => (
                                      <button key={r.slug} onClick={() => { navigate(`/region/${r.slug}`); setOpenDropdown(null); setShowRégions(false); }}
                                        className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:text-[#1e3a5f] hover:bg-[#eaf6fc]/60 transition-colors flex items-center gap-3"
                                        data-testid={`region-${r.slug}`}
                                      >
                                        <MapPin size={13} className="text-[#35AFE4]/50 flex-shrink-0" />
                                        {r.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : child.isCta ? (
                            <div key={child.to}>
                              <div className="mx-3 my-1.5 border-t border-gray-100" />
                              <button onClick={() => { navigate(child.to); setOpenDropdown(null); }}
                                className="w-full text-left mx-2 px-3 py-2 text-[13px] font-bold text-[#35AFE4] hover:text-white hover:bg-[#35AFE4] transition-all rounded-lg flex items-center gap-3 group/cta"
                                style={{ width: 'calc(100% - 16px)' }}
                                data-testid="submenu-tous-les-articles"
                              >
                                <ArrowRight size={15} className="flex-shrink-0 group-hover/cta:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                                <span>{child.label}</span>
                              </button>
                            </div>
                          ) : (
                            <button key={child.to} onClick={() => { navigate(child.to); setOpenDropdown(null); }}
                              className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:text-[#1e3a5f] hover:bg-[#eaf6fc]/60 transition-colors flex items-center gap-3"
                              data-testid={`submenu-${(child.to || '').split('?')[0].replace(/\//g, '-').substring(1) || child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {child.icon && <child.icon size={15} className="text-[#35AFE4] flex-shrink-0" strokeWidth={2} />}
                              <span>{child.label}</span>
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right actions */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Forum - visible uniquement connecte */}
              {user && (
                <button onClick={() => navigate('/messagerie')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${
                    isTransparent
                      ? 'bg-white/15 text-white hover:bg-white/25'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`} data-testid="btn-forum">
                  <MessageSquare size={14} strokeWidth={2.4} />
                  Forum
                </button>
              )}

              {/* Espace Patients */}
              {!isHidden('/patients') && <button onClick={() => navigate('/patients')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${
                  isTransparent
                    ? 'bg-white/15 text-white hover:bg-white/25'
                    : 'bg-pink-50 text-pink-600 hover:bg-pink-100'
                }`} data-testid="btn-espace-patients">
                <UserRound size={14} strokeWidth={2.4} />
                Espace Patients
              </button>}

              {/* Annuaire */}
              {!isHidden('/annuaire') && <button onClick={() => navigate('/annuaire')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold tracking-wider transition-all ${
                  isTransparent
                    ? 'bg-white/15 text-white hover:bg-white/25'
                    : 'bg-[#35AFE4]/10 text-[#1e3a5f] hover:bg-[#35AFE4]/20'
                }`}
                data-testid="btn-annuaire">
                <Stethoscope size={14} strokeWidth={2.4} className={isTransparent ? '' : 'text-[#35AFE4]'} />
                Annuaire
              </button>}

              {/* Install App */}
              {canInstall && (
                <button onClick={triggerInstall}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold tracking-wider transition-all border ${
                    isTransparent
                      ? 'border-white/30 text-white/90 hover:bg-white/10'
                      : 'border-[#35AFE4]/30 text-[#35AFE4] hover:bg-[#eaf6fc]'
                  }`}
                  data-testid="btn-install-app">
                  <Download size={13} strokeWidth={2.5} />
                  Installer
                </button>
              )}

              {/* User area */}
              {user ? (
                <div className="relative group">
                  <button className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full transition-all border"
                    style={{
                      background: isTransparent ? 'rgba(255,255,255,0.12)' : '#e0f2fe',
                      borderColor: isTransparent ? 'rgba(125,211,252,0.6)' : '#7dd3fc',
                    }}
                    data-testid="header-user-btn">
                    {(user.profile_image) ? (
                      <img src={user.profile_image}
                        alt={user.first_name} className="w-10 h-10 rounded-full object-cover ring-2 ring-sky-300" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold ring-2 ring-sky-200"
                        style={{ background: 'linear-gradient(135deg, #38bdf8, #7dd3fc)' }}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                    )}
                    <span className={`text-sm font-medium ${isTransparent ? 'text-white' : 'text-sky-700'}`}>
                      {user.first_name}
                    </span>
                    <ChevronDown size={12} className={isTransparent ? 'text-white/60' : 'text-sky-400'} />
                  </button>
                  {/* Dropdown */}
                  <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 min-w-[200px]">
                      <div className="px-4 py-2.5 border-b border-gray-50">
                        <p className="text-sm font-bold text-gray-900">{user.first_name} {user.last_name}</p>
                        <p className="text-[10px] text-gray-400 capitalize">{user.role?.replace('_', ' ')}</p>
                      </div>
                      {isAdmin && (
                        <button onClick={() => navigate('/admin')} className="w-full text-left px-4 py-2.5 text-sm font-medium text-[#1e3a5f] hover:bg-[#1e3a5f]/5 flex items-center gap-2.5" data-testid="dropdown-admin">
                          <LayoutDashboard size={15} /> Back Office Admin
                        </button>
                      )}
                      <button onClick={() => navigate('/dashboard')} className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-[#35AFE4] hover:bg-[#eaf6fc]/50 flex items-center gap-2.5" data-testid="dropdown-dashboard">
                        <Settings size={15} /> Mon compte
                      </button>
                      {(isAdmin || isOphtalmo) && (
                        <button onClick={() => navigate('/mes-fiches')} className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-[#35AFE4] hover:bg-[#eaf6fc]/50 flex items-center gap-2.5" data-testid="dropdown-fiches">
                          <MapPin size={15} /> Mes fiches annuaire
                        </button>
                      )}
                      {isOphtalmo && !isAdmin && (
                        <button onClick={() => navigate('/adhesion')} className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-[#1e3a5f] hover:bg-gray-50 flex items-center gap-2.5" data-testid="dropdown-adhesion">
                          <UserPlus size={15} /> Mon adhésion
                        </button>
                      )}
                      <button onClick={() => navigate('/mon-support')} className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-[#35AFE4] hover:bg-[#eaf6fc]/50 flex items-center gap-2.5" data-testid="dropdown-support">
                        <Headphones size={15} /> Support / Tickets
                      </button>
                      <div className="border-t border-gray-50 mt-1 pt-1">
                        <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm font-medium text-red-400 hover:text-red-600 hover:bg-red-50 flex items-center gap-2.5" data-testid="dropdown-logout">
                          <LogOut size={15} /> Déconnexion
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <button onClick={() => navigate('/auth')}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-sm"
                  style={{ background: isTransparent ? 'rgba(125,211,252,0.85)' : '#7dd3fc', boxShadow: '0 0 0 2px rgba(125,211,252,0.3)' }}
                  data-testid="header-user-btn">
                  <User size={18} className="text-white" />
                </button>
              )}
            </div>

            {/* Mobile actions: pills with colored labels */}
            <div className="lg:hidden flex items-center gap-1 sm:gap-1.5">
              {!isHidden('/patients') && <button onClick={() => navigate('/patients')}
                className="hidden min-[440px]:flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-pink-50 hover:bg-pink-100 transition-colors"
                aria-label="Espace Patients"
                data-testid="mobile-btn-patients">
                <UserRound size={16} className="text-pink-500" strokeWidth={2.4} />
                <span className="text-xs font-bold text-pink-600 whitespace-nowrap">Espace Patients</span>
              </button>}
              {!isHidden('/annuaire') && <button onClick={() => navigate('/annuaire')}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-[#35AFE4]/10 hover:bg-[#35AFE4]/20 transition-colors"
                aria-label="Annuaire des praticiens"
                data-testid="mobile-btn-annuaire">
                <Stethoscope size={16} className="text-[#35AFE4]" strokeWidth={2.4} />
                <span className="text-xs font-bold text-[#1e3a5f]">Annuaire</span>
              </button>}
              {user && (isOphtalmo || isAdmin) && (
                <button onClick={() => navigate('/messagerie')}
                  className="hidden min-[600px]:flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 transition-colors"
                  aria-label="Messagerie"
                  data-testid="mobile-btn-forum">
                  <MessageSquare size={16} className="text-emerald-600" strokeWidth={2.4} />
                  <span className="text-xs font-bold text-emerald-700">Messagerie</span>
                </button>
              )}
              {user ? (
                <button onClick={() => navigate('/dashboard')}
                  className="p-0.5 rounded-full hover:opacity-90 transition-all ml-0.5"
                  aria-label="Mon compte"
                  data-testid="mobile-btn-account">
                  {user.profile_image ? (
                    <img src={user.profile_image} alt={user.first_name}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-sky-300" />
                  ) : (
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs font-bold ring-2 ring-sky-200"
                      style={{ background: 'linear-gradient(135deg, #38bdf8, #7dd3fc)' }}>
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                  )}
                </button>
              ) : (
                <button onClick={() => navigate('/auth')}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 shadow-sm ml-0.5"
                  style={{ background: '#7dd3fc', boxShadow: '0 0 0 2px rgba(125,211,252,0.3)' }}
                  aria-label="Se connecter"
                  data-testid="mobile-btn-login">
                  <User size={16} className="text-white" />
                </button>
              )}
              <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1.5 sm:p-2 rounded-lg ml-0.5" data-testid="mobile-menu-toggle">
                {mobileOpen
                  ? <X size={20} className={isTransparent ? 'text-white' : 'text-gray-700'} />
                  : <Menu size={20} className={isTransparent ? 'text-white' : 'text-gray-700'} />
                }
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              {/* TOP QUICK ACTIONS — account, forum, contact */}
              <div className="space-y-1.5 pb-4 border-b border-gray-100">
                {user ? (
                  <button onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#a8d5ba]/30 to-[#a8d5ba]/10 hover:from-[#a8d5ba]/50 transition-all"
                    data-testid="mobile-menu-account">
                    {user.profile_image ? (
                      <img src={user.profile_image} alt={user.first_name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #8ecda8, #a8d5ba)' }}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <p className="text-sm font-bold text-[#1e3a5f]">Mon compte</p>
                      <p className="text-[11px] text-gray-500">{user.first_name} {user.last_name}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                ) : (
                  <div className="space-y-1.5" data-testid="mobile-auth-row">
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => { navigate('/adhesion#ophtalmo'); setMobileOpen(false); }}
                        className="flex flex-col items-center gap-1 px-3 py-3 rounded-xl bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-bold text-center transition-colors"
                        data-testid="mobile-menu-adhere-ophtalmo">
                        <Stethoscope size={20} className="text-sky-600" strokeWidth={2.2} />
                        <span className="leading-tight">Adhérer<br/>Ophtalmologiste</span>
                      </button>
                      <button onClick={() => { navigate('/adhesion#intern'); setMobileOpen(false); }}
                        className="flex flex-col items-center gap-1 px-3 py-3 rounded-xl bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-bold text-center transition-colors"
                        data-testid="mobile-menu-adhere-intern">
                        <GraduationCap size={20} className="text-sky-600" strokeWidth={2.2} />
                        <span className="leading-tight">Adhérer<br/>Interne</span>
                      </button>
                    </div>
                    <button onClick={() => { navigate('/messagerie'); setMobileOpen(false); }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-bold transition-colors"
                      data-testid="mobile-menu-forum-guest">
                      <MessageSquare size={16} strokeWidth={2.4} />
                      Forum
                    </button>
                  </div>
                )}
                {user && (isOphtalmo || isAdmin) && (
                  <button onClick={() => { navigate('/messagerie'); setMobileOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors">
                    <MessageSquare size={18} className="text-emerald-600 flex-shrink-0" strokeWidth={2.4} />
                    <span className="flex-1 text-left text-base font-bold text-emerald-700">Messagerie</span>
                    <ChevronRight size={16} className="text-emerald-400" />
                  </button>
                )}
                <button onClick={() => { navigate('/contact'); setMobileOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#35AFE4]/10 hover:bg-[#35AFE4]/20 transition-colors">
                  <Bell size={18} className="text-[#35AFE4] flex-shrink-0" strokeWidth={2.4} />
                  <span className="flex-1 text-left text-base font-bold text-[#1e3a5f]">Contact</span>
                  <ChevronRight size={16} className="text-[#35AFE4]/60" />
                </button>
              </div>

              {/* SECTIONS */}
              {menuItems.map((item) => (
                <div key={item.label} className="pt-3">
                  <p className="px-3 pb-2 text-xs font-bold text-[#35AFE4] uppercase tracking-widest">{item.label}</p>
                  {item.children.map((child) => (
                    child.hasSubmenu ? (
                      <div key="regions-mobile">
                        <button onClick={() => setMobileRegionsOpen(!mobileRegionsOpen)}
                          className="w-full flex items-center justify-between px-3 py-3 text-base font-semibold text-gray-700 hover:text-[#1e3a5f] hover:bg-gray-50 rounded-lg"
                          data-testid="mobile-regions-toggle">
                          <span className="flex items-center gap-2.5">
                            <MapPin size={16} className="text-[#35AFE4]" /> Les régions
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#35AFE4]/10 text-[#35AFE4]">{REGIONS.length}</span>
                          </span>
                          <ChevronDown size={16} className={`text-gray-400 transition-transform ${mobileRegionsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {mobileRegionsOpen && (
                          <div className="ml-2 pl-4 border-l-2 border-[#35AFE4]/20 mt-1 space-y-0.5">
                            {REGIONS.map(r => (
                              <button key={r.slug} onClick={() => { navigate(`/region/${r.slug}`); setMobileOpen(false); setMobileRegionsOpen(false); }}
                                className="w-full text-left px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#1e3a5f] hover:bg-gray-50 rounded-lg">
                                {r.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : child.isCta ? (
                      <button key={child.to} onClick={() => { navigate(child.to); setMobileOpen(false); }}
                        className="w-full text-left px-3 py-3 text-base font-bold text-[#35AFE4] hover:bg-[#eaf6fc] rounded-lg flex items-center gap-2.5 mt-1 border-t border-gray-100 pt-3">
                        {child.icon && <child.icon size={16} />} {child.label}
                      </button>
                    ) : (
                      <button key={child.to} onClick={() => { navigate(child.to); setMobileOpen(false); }}
                        className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:text-[#1e3a5f] hover:bg-gray-50 rounded-lg flex items-center gap-2.5">
                        {child.icon && <child.icon size={16} className="text-[#35AFE4]/70" />} {child.label}
                      </button>
                    )
                  ))}
                </div>
              ))}

              <div className="border-t border-gray-100 pt-3 mt-3 space-y-1">
                <button onClick={() => { navigate('/patients'); setMobileOpen(false); }}
                  className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-pink-50 rounded-lg flex items-center gap-2.5">
                  <UserRound size={16} className="text-pink-500" /> Espace Patients
                </button>
                <button onClick={() => { navigate('/annuaire'); setMobileOpen(false); }}
                  className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-[#35AFE4]/10 rounded-lg flex items-center gap-2.5">
                  <Stethoscope size={16} className="text-[#35AFE4]" /> Annuaire des Praticiens
                </button>
                {user && user.role !== 'intern' && (
                  <button onClick={() => { navigate('/adhesion'); setMobileOpen(false); }}
                    className="w-full text-left px-3 py-3 text-base font-bold text-sky-800 hover:bg-sky-100 bg-sky-50 rounded-lg flex items-center gap-2.5">
                    <UserPlus size={16} className="text-sky-600" /> Adhérer / Renouveler
                  </button>
                )}
                {canInstall && (
                  <button onClick={() => { triggerInstall(); setMobileOpen(false); }}
                    className="w-full text-left px-3 py-3 text-base font-bold text-[#35AFE4] hover:bg-[#eaf6fc] rounded-lg flex items-center gap-2.5"
                    data-testid="mobile-install-app">
                    <Download size={16} /> Installer l'application
                  </button>
                )}
              </div>

              {/* BOTTOM USER ACTIONS (only when logged in) */}
              {user && (
                <div className="border-t border-gray-100 pt-3 mt-3 space-y-1">
                  {isAdmin && (
                    <button onClick={() => { navigate('/admin'); setMobileOpen(false); }}
                      className="w-full text-left px-3 py-3 text-base font-medium text-[#1e3a5f] hover:bg-[#1e3a5f]/5 rounded-lg flex items-center gap-2.5">
                      <LayoutDashboard size={16} /> Back Office Admin
                    </button>
                  )}
                  {(isAdmin || isOphtalmo) && (
                    <button onClick={() => { navigate('/mes-fiches'); setMobileOpen(false); }}
                      className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2.5">
                      <MapPin size={16} /> Mes fiches annuaire
                    </button>
                  )}
                  <button onClick={() => { navigate('/mon-support'); setMobileOpen(false); }}
                    className="w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2.5">
                    <Headphones size={16} /> Support / Tickets
                  </button>
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="w-full text-left px-3 py-3 text-base font-medium text-red-500 hover:bg-red-50 rounded-lg flex items-center gap-2.5">
                    <LogOut size={16} /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Spacer — always show since header is always fixed white */}
      <div className="h-16 lg:h-[72px]" />
    </>
  );
};

export default SiteHeader;
